
<script>
import { defineComponent, computed, inject } from 'vue'
import '@kit/assets/css/quote_feature.css'
import { pathname } from '@kit/utils/Formats'
import { useStore } from 'vuex'
import { cmsasset } from '@kit/utils/Formats'
import { slugPageSetup } from '@kit/utils/Setup' 
import '@css/index.css'
import Column from '@kit/components/Column.vue'
import Shine from '@kit/components/Shine.vue'
import Divider from '@project/components/Divider.vue'
import { sunriseProteaFamilyRolloverInfoImg, xLazyTheme } from '@project/themes'
import RolloverInfoImg from '@kit/components/RolloverInfoImg.vue'
import Img from '@kit/components/Img.vue'



export default defineComponent({
  name: "Varieties",

  setup() {

    const store = useStore()
    const themer = inject("themer")
    const sunriseProteaFamilyRolloverInfoImg_ = themer(sunriseProteaFamilyRolloverInfoImg)
    const xLazyTheme_ = themer(xLazyTheme)

    const inMobile = computed(() => {
      return store.state.inMobile
    })
    
    const { id, prefetch } = slugPageSetup({
      routeParam: 'slug',
      prefetchKey: 'variety',
      prefetchExtra: ['home'],
      metadata(variety, meta) {

        const seo = variety.variety.seo_set ? variety.variety.seo_set[0] : null

        if(seo) {

          meta.addCanonical()
          meta.addTitle('Sunrise Protea | '+seo.page_title)
          meta.addMetaItem([
            { name:'description', content: seo.page_description },
            { name:'og:title', content: seo.open_graph_title },
            { name:'og:description', content: seo.open_graph_description }
          ])

          if(seo.open_graph_image) {
            meta.addMetaItem([
              { name:"og:image", content: cmsasset(seo.open_graph_image.guid) },
              { name:"og:image:alt", content: seo.open_graph_image_alt },
            ])
          }

        }

      }

    })

    const varietyRows = computed(() => {
      const famArr = prefetch.value ? prefetch.value.variety.flowers : null
      if(!famArr) {
        return []
      } else {
        const len = famArr.length;
        let stack = 0
        const limit = 2
        let rows = []
        let currentRow = []
        for(let i=0; i<len; i++) {
          const famEl = famArr[i]
          stack++
          if(stack <= limit) {
            currentRow.push(famEl)
          }  
          if(stack == limit) {
            rows.push(currentRow)
            currentRow = []
            stack = 0
          }
        }

        if(currentRow.length > 0) {
          rows.push(currentRow)
        }

        return rows
      }

    })





    return { 
      id,
      cmsasset,
      pathname, 
      prefetch, 
      inMobile,
      varietyRows,
      sunriseProteaFamilyRolloverInfoImg_,
      xLazyTheme_
    }
  },


  components: {
    Img,
    Column,
    Shine,
    Divider,
    RolloverInfoImg
  },
});
</script>

<style scoped>

.flower-tile-main-text {
    color:white; 
    line-height:1.7; 
    font-size:1.1rem;
  }
  @media (max-width : 960px) {
    .flower-tile-main-text {
      line-height:1.7; 
      font-size:1.1rem;
    }
  }

  @media (max-width : 600px) {
    .flower-tile-main-text {
      line-height:1.7; 
      font-size:0.9rem;
    }

  }

  .single-rollover-in-row {
    width: 50% !important;
    padding:0px;
  }

  @media (max-width : 960px) {
    .single-rollover-in-row {
      width:100% !important;
    }
  }

</style>
<style>
  .srp-RolloverInfoImg-offContent {
    padding:0px;
  }
</style>


<template>
 
  <main class="sb sb-v sb-greedy page-margin-top" role="main">

    <Column v-if="prefetch && prefetch.variety && prefetch.home" ltD="60" gtB="700" gtH="700" tag="article" class="sb-v sb-g30" style="margin-bottom:30px;">
      
      <h1 class="sb sb-text">Variety: {{ prefetch.variety.variety.display_title }}</h1>
      <Img 
        v-if="prefetch.variety.variety.primary_flower_image"
        class="sb sb-explicit" 
        :src="cmsasset(prefetch.variety.variety.primary_flower_image[0].image.guid, 1536)" 
        :theme="xLazyTheme_"/>
      
      <p class="sb sb-text" v-html="prefetch.variety.variety.main_text"></p>

      <Divider/>

      <h2 class="sb sb-text">Plants in the {{ prefetch.variety.variety.display_title.toLowerCase() }} variety</h2>

      <template v-for="(flowerRow, i) in varietyRows">

        <div v-if="flowerRow.length == 2" aria-role="list" class="sb sb-h sb-ltm-h2v sb-uniform-2">
          
          <template v-for="(flower, j) in flowerRow" :key="i+'-'+j">
            <RolloverInfoImg aria-role="list-item" 
              outerClasses="sb-v sb-uniform sb-gr sb-g10"
              tag="router-link"
              :to="`/flower/${flower.url_slug}`"
              :theme="sunriseProteaFamilyRolloverInfoImg_" 
              :imgAriaLabel="flower.flower_image[0].aria_label"
              :imgAriaDescribedBy="`variety-flower-area-${i}-${j}-on variety-flower-area-${i}-${j}-off`"
              :src="cmsasset(flower.flower_image[0].image.guid, 864)"
              :coverCSS="`background-color:${flower.flower_image[0].rollover_rgba}`"
              >
                <template v-slot:onContent>
                  <p :id="`variety-flower-area-${i}-${j}-on`" class="sb sb-text flower-tile-main-text" v-html="flower.short_text"></p>
                </template>
                <template v-slot:offContent>
                  <div :id="`variety-flower-area-${i}-${j}-off`" class="sb sb-v" :style="`background-color:${flower.flower_image[0].rollover_title_rgba}; color:${flower.flower_image[0].rollover_title_color || '#FFFFFF' }; padding:20px; margin:0px;`">
                    <h3 class="sb sb-text"><b>{{ flower.display_title }}</b></h3>
                    <h5 v-if="flower.available">This plant is available:<br>{{ prefetch.home.availability_window  }}</h5>
                  </div>
                </template>
            </RolloverInfoImg> 
          </template>

        </div>
        <div v-else aria-role="list" class="sb sb-h sb-ltm-h2v sb-align-ch">

          <template v-for="(flower, k) in flowerRow" :key="k">

            <RolloverInfoImg
              aria-role="list-item"
              outerClasses="sb-v single-rollover-in-row sb-explicit sb-gr sb-g10"
              tag="router-link"
              :to="`/flower/${flower.url_slug}`"
              :theme="sunriseProteaFamilyRolloverInfoImg_" 
              :imageAriaLabel="flower.flower_image[0].aria_label"
              :src="cmsasset(flower.flower_image[0].image.guid, 864)"
              :coverCSS="`background-color:${flower.flower_image[0].rollover_rgba}`"
              >
                <template v-slot:onContent>
                  <p class="sb sb-text flower-tile-main-text" v-html="flower.short_text"></p>
                </template>
                <template v-slot:offContent>
                  <div class="sb sb-v" :style="`background-color:${flower.flower_image[0].rollover_title_rgba}; color:${flower.flower_image[0].rollover_title_color || '#FFFFFF' }; padding:20px; margin:0px;`">
                    <h3 class="sb sb-text"><b>{{ flower.display_title }}</b></h3>
                    <h5 v-if="flower.available">This plant is available:<br>{{ prefetch.home.availability_window  }}</h5>
                  </div>
                </template>
            </RolloverInfoImg> 
          </template>

        </div>

      </template>


    </Column>
    <Column v-else ltD="60" gtB="700" gtH="700" tag="article" class="sb-v sb-g30" style="margin-bottom:30px;">
      <Shine min="100px" h="100px" shine="light" />
      <Shine min="100px" h="100px" shine="light" />
      <Shine min="100px" h="100px" shine="light" />
    </Column>

    <Divider/>


  </main>

</template>
